import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap, Wallet, Gift, BookOpen, Calendar,
  Plus, User, MapPin, CheckCircle
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import MarkFulfilledModal from '../../components/MarkFulfilledModal';
import {
  DashboardShell,
  StatsCard,
  ApplicationsTab,
  PurchasedContactsTab,
  MeetingsTab,
  WalletTab,
  ReferralsTab,
} from '../../components/dashboard';
import {
  getMyStudentProfile,
  getWalletBalance,
  getWalletTransactions,
  getReferralStats,
  getMyTuitionPosts,
  getMyMeetings,
  getMyPurchasedContacts,
  getMyApplications
} from '../../services/authApi';

export const StudentDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [referralStats, setReferralStats] = useState(null);
  const [tuitionPosts, setTuitionPosts] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [purchasedContacts, setPurchasedContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fulfilledModal, setFulfilledModal] = useState({ open: false, type: '', id: '', title: '' });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      try { const p = await getMyStudentProfile(); setProfile(p); } catch { }
      const w = await getWalletBalance(); setWalletBalance(w.balance);
      try { const r = await getReferralStats(); setReferralStats(r); } catch { }
      try { const t = await getMyTuitionPosts(); setTuitionPosts(t.posts || []); } catch { }
      try { const m = await getMyMeetings(); setMeetings(m.meetings || m || []); } catch { }
      try { const c = await getMyPurchasedContacts('all'); setPurchasedContacts(c.purchases || []); } catch { }
      try { const a = await getMyApplications(); setApplications(a.applications || []); } catch { }
      try { const tx = await getWalletTransactions(); setWalletTransactions(tx.transactions || []); } catch { }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <DashboardShell
      title="Student Dashboard"
      subtitle={user.email}
      icon={GraduationCap}
      iconBg="bg-blue-600"
      onLogout={handleLogout}
    >
      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <StatsCard title="Wallet Balance" value={`${walletBalance} coins`} subtitle="1 coin = ₹1" icon={Wallet} iconColor="text-blue-600" />
        {referralStats && (
          <StatsCard title="Referrals" value={referralStats.total_referrals} subtitle={`${referralStats.total_coins_earned} coins earned`} icon={Gift} iconColor="text-green-600" />
        )}
        <StatsCard title="Tuition Posts" value={tuitionPosts.length} subtitle="Active requirements" icon={BookOpen} iconColor="text-purple-600" />
        <StatsCard title="Meetings" value={meetings.length} subtitle="Scheduled sessions" icon={Calendar} iconColor="text-orange-600" />
      </div>

      {/* Profile prompt */}
      {!profile && (
        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <User className="h-5 w-5 text-blue-600" />
              <span>Complete Your Profile</span>
            </CardTitle>
            <CardDescription>
              Create your student profile to start posting tuition requirements and connect with tutors
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/profile/create')} className="bg-blue-600 hover:bg-blue-700">
              Create Profile
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="tuition-posts">Tuition Posts</TabsTrigger>
          <TabsTrigger value="applications">Applications ({applications.length})</TabsTrigger>
          <TabsTrigger value="purchased">Purchased ({purchasedContacts.length})</TabsTrigger>
          <TabsTrigger value="meetings">Meetings</TabsTrigger>
          <TabsTrigger value="wallet">Wallet</TabsTrigger>
          <TabsTrigger value="referrals">Referrals</TabsTrigger>
          {profile && <TabsTrigger value="profile">Profile</TabsTrigger>}
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-4">
              <Button onClick={() => navigate('/find-tutor')} variant="outline" className="h-24 flex-col">
                <GraduationCap className="h-8 w-8 mb-2 text-blue-600" />
                <span>Find Tutors</span>
              </Button>
              <Button onClick={() => navigate('/tuition-posts/create')} variant="outline" className="h-24 flex-col">
                <Plus className="h-8 w-8 mb-2 text-green-600" />
                <span>Post Requirement</span>
              </Button>
              <Button onClick={() => navigate('/find-job')} variant="outline" className="h-24 flex-col">
                <BookOpen className="h-8 w-8 mb-2 text-purple-600" />
                <span>Browse Jobs</span>
              </Button>
            </CardContent>
          </Card>
          {profile && (
            <Card>
              <CardHeader><CardTitle>Your Information</CardTitle></CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><p className="text-sm text-gray-600">Full Name</p><p className="font-medium">{profile.full_name}</p></div>
                  <div><p className="text-sm text-gray-600">School</p><p className="font-medium">{profile.school_name}</p></div>
                  <div><p className="text-sm text-gray-600">Class</p><p className="font-medium">{profile.grade}</p></div>
                  <div><p className="text-sm text-gray-600">Board</p><p className="font-medium">{profile.board}</p></div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tuition Posts */}
        <TabsContent value="tuition-posts">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>My Tuition Requirements</CardTitle>
                <Button onClick={() => navigate('/tuition-posts/create')} size="sm">
                  <Plus className="h-4 w-4 mr-2" /> New Post
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {tuitionPosts.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No tuition posts yet</p>
                  <Button onClick={() => navigate('/tuition-posts/create')} size="sm" className="mt-4">Create Your First Post</Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {tuitionPosts.map((post) => (
                    <div key={post.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold">{post.category}</h3>
                        <div className="flex items-center space-x-2">
                          <Badge variant={post.status === 'active' ? 'default' : 'secondary'}>{post.status}</Badge>
                          {post.status === 'active' && (
                            <Button variant="outline" size="sm" className="text-green-600 hover:text-green-700"
                              onClick={() => setFulfilledModal({ open: true, type: 'tuition', id: post.id, title: post.category })}>
                              <CheckCircle className="h-4 w-4 mr-1" /> Mark Fulfilled
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => navigate(`/tuition-posts/${post.id}/edit`)}>Edit</Button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{post.description}</p>
                      <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                        <span>Budget: ₹{post.budget_amount}/{post.budget_type}</span>
                        <span>•</span>
                        <span>Mode: {post.class_mode}</span>
                        <span>•</span>
                        <span>{post.applications_count} applications</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Shared Tabs */}
        <TabsContent value="applications"><ApplicationsTab applications={applications} /></TabsContent>
        <TabsContent value="purchased"><PurchasedContactsTab purchasedContacts={purchasedContacts} /></TabsContent>
        <TabsContent value="meetings"><MeetingsTab meetings={meetings} /></TabsContent>
        <TabsContent value="wallet"><WalletTab walletBalance={walletBalance} walletTransactions={walletTransactions} onNavigateWallet={() => navigate('/wallet')} /></TabsContent>
        <TabsContent value="referrals"><ReferralsTab referralStats={referralStats} /></TabsContent>

        {/* Profile Tab */}
        {profile && (
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Student Profile</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => navigate('/profile/edit')}>Edit Profile</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold mb-4">Personal Information</h3>
                    <div className="space-y-3">
                      <div><p className="text-sm text-gray-600">Full Name</p><p className="font-medium">{profile.full_name}</p></div>
                      <div><p className="text-sm text-gray-600">Username</p><p className="font-medium">{profile.username}</p></div>
                      <div><p className="text-sm text-gray-600">Date of Birth</p><p className="font-medium">{profile.dob}</p></div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-4">Academic Information</h3>
                    <div className="space-y-3">
                      <div><p className="text-sm text-gray-600">School</p><p className="font-medium">{profile.school_name}</p></div>
                      <div><p className="text-sm text-gray-600">Class</p><p className="font-medium">{profile.grade}</p></div>
                      <div><p className="text-sm text-gray-600">Board</p><p className="font-medium">{profile.board}</p></div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-4">Parent Information</h3>
                    <div className="space-y-3">
                      <div><p className="text-sm text-gray-600">Parent Name</p><p className="font-medium">{profile.parent_name}</p></div>
                      <div><p className="text-sm text-gray-600">Parent Mobile</p><p className="font-medium">{profile.parent_mobile}</p></div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-4">Contact Information</h3>
                    <div className="space-y-3">
                      <div className="flex items-start space-x-2">
                        <MapPin className="h-4 w-4 text-gray-600 mt-1" />
                        <div>
                          <p className="text-sm text-gray-600">Address</p>
                          <p className="font-medium">{profile.address}</p>
                          <p className="text-sm text-gray-600">{profile.city}, {profile.state}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Mark as Fulfilled Modal */}
      <MarkFulfilledModal
        isOpen={fulfilledModal.open}
        onClose={() => setFulfilledModal({ open: false, type: '', id: '', title: '' })}
        contentType={fulfilledModal.type}
        contentId={fulfilledModal.id}
        contentTitle={fulfilledModal.title}
        token={token}
        onSuccess={() => {
          getMyTuitionPosts()
            .then(data => setTuitionPosts(data.posts || []))
            .catch(err => console.error('Error refreshing tuition posts:', err));
        }}
      />
    </DashboardShell>
  );
};
